import { transition } from '@ifixit/primitives';
import {
   Box,
   Tabs,
   TabList,
   Tab,
   TabPanels,
   TabPanel,
   Badge,
   StyleProps,
   useBreakpointValue,
} from '@chakra-ui/react';
import { FaIcon } from '@ifixit/icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/*
 * Custom reusable Chakra Tabs component with site styling.
 *
 * Props:
 *   - activeTab: Index of highlighted tab
 *   - tabsData: Array of TabObject (see interface below). Supports optional icons and badges.
 *   - variant: Tab style (default: 'soft-rounded')
 *   - colorScheme: Color scheme (default: 'gray')
 *   - orientation: 'horizontal' or 'vertical'
 *   - cardGap: Gap spacing between tabs
 *   - context: Class name prefix for test selectors
 *
 * Note: TabPanels are hidden (display: none) but kept for accessibility.
 */

export interface TabObject {
   label: string;
   handler: () => void;
   panel: HTMLElement;
   badge?: number;
   icon?: IconDefinition;
   id?: string;
}

type PrettyTabsProps = {
   activeTab: number;
   tabsData: TabObject[];
   context?: string;
   variant?: 'soft-rounded';
   colorScheme?: 'gray' | 'brand';
   style?: StyleProps;
   cardGap?: string;
};

export default function PrettyTabs({
   activeTab,
   tabsData,
   context,
   variant = 'soft-rounded',
   colorScheme = 'gray',
   cardGap,
   style,
}: PrettyTabsProps) {
   return (
      <Box className="cardTabs gradient-overflow">
         <Tabs
            isLazy
            index={activeTab}
            variant={variant}
            colorScheme={colorScheme}
            orientation={useBreakpointValue({ md: 'vertical' })}
            {...style}
         >
            <TabList className="gradient-overflow-inner" flex={1} py={2} gap={cardGap}>
               {listTabComponents(tabsData, activeTab, context)}
            </TabList>
            <TabPanels display="none">{listTabPanelComponents(tabsData)}</TabPanels>
         </Tabs>
      </Box>
   );
}

function listTabPanelComponents(tabsData: TabObject[]) {
   return tabsData.map(tabObject => <TabPanel key={tabObject.label}></TabPanel>);
}

function listTabComponents(tabsData: TabObject[], activeTab: number, context?: string) {
   return tabsData.map((tabObject, index) => {
      const badge = tabObject.badge;
      const icon = tabObject.icon;
      const tabId = tabObject.id || tabObject.label;
      const baseClass = context ? `${tabId} ${context}` : `${tabId}`;

      return (
         <Tab
            className={index === activeTab ? `active ${baseClass}` : baseClass}
            key={index}
            onClick={tabObject.handler}
            id={tabId}
            borderRadius="20px"
            borderWidth="1px"
            color="currentColor"
            display="flex"
            alignItems="center"
            fontWeight="500"
            maxHeight="48px"
            py={{ base: 1, md: 3 }}
            px={3}
            justifyContent="start"
            gap={2}
            whiteSpace="nowrap"
            transition={`color ${transition[150]}, border-color ${transition[150]}`}
            sx={{
               '&.active': {
                  backgroundColor: 'gray.100',
                  borderColor: 'gray.100',
                  cursor: 'default',
               },
               '@media (hover: hover)': {
                  '&:hover:not(.active)': {
                     borderColor: 'gray.400',
                     color: 'brand.500',
                  },
               },
            }}
         >
            {icon && <FaIcon icon={icon} />}
            {tabObject.label}
            {badge ? (
               <Badge
                  fontSize="12px"
                  fontWeight="semibold"
                  borderRadius="full"
                  colorScheme="brand"
                  variant="solid"
                  py="0"
                  px="5.25px"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
               >
                  {badge}
               </Badge>
            ) : null}
         </Tab>
      );
   });
}
